import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "reactstrap";
import {
  GeckoDialogService,
  GeckoIcon,
  GECKO_CONSTS,
} from "@geckolabs/gecko-react-ui";
import {
  MESSAGE_REMOVED_EVENT,
  MESSAGE_HIGHLIGHTED_EVENT,
  ATTENDEE_EJECTED_EVENT,
} from "../../../../constants";
import { AttendanceService } from "../../../../services/AttendanceService";
import { AdminService } from "../../../../services/AdminService";
import { DataService } from "../../../../services/DataService";
import { ContactService } from "../../../../services/ContactService";
import { EventService } from "../../../../services/EventService";
import { PresenceSocketService } from "../../../../services/PresenceSocketService";
import { VideoService } from "../../../../services/VideoService";
import DateUtils from "../../../../../../services/DateUtils";
import { __, STR_KEY } from "../../../../../../services/Translate";
import { $stateParams } from "../../../../../../router";
import HtmlWrapper from "../../../../../../components/HtmlWrapper";

export const Message = ({
  message,
  i,
  isHost,
  isAdmin,
  setReplyTo,
  focus,
  setFocusMessage,
}) => {
  const messageRef = useRef();

  useEffect(() => {
    if (focus) {
      messageRef.current?.focus();
    }
  }, [focus]);

  const messageSharedProps = {
    setReplyTo,
    setFocusMessage,
  };

  return (
    <div className="conversation-message mb-3" key={i}>
      {message.reply_to_message && (
        <MessageItem
          {...{
            isAdmin: message.reply_to_message.is_admin,
            isHost: message.reply_to_message.is_host,
            message: message.reply_to_message,
            isLinkedMessage: true,
            ...messageSharedProps,
          }}
        />
      )}
      <MessageItem
        {...{
          focus,
          messageRef,
          isAdmin,
          isHost,
          message,
          ...messageSharedProps,
        }}
      />
    </div>
  );
};

const MessageItem = ({
  message,
  isLinkedMessage,
  setReplyTo,
  isAdmin,
  isHost,
  messageRef,
  setFocusMessage,
  focus,
}) => {
  return (
    <AdminControl {...{ message, setReplyTo, isLinkedMessage }}>
      <div
        className="conversation-message-container col"
        onClick={() => {
          if (isLinkedMessage) {
            setFocusMessage(message.uuid);
          }
        }}
      >
        <div className="conversation-message-wrapper d-flex justify-content-start align-items-end">
          <div
            ref={messageRef}
            className={`conversation-message-content rounded position-relative d-inline-block ${
              isAdmin || isHost ? "admin-bg" : ""
            } ${isLinkedMessage ? "linked-message" : ""}`}
            tabIndex="0"
            onBlur={() => {
              if (focus) {
                setFocusMessage(null);
              }
            }}
          >
            {isLinkedMessage && <span className="linked-message-trail" />}

            <span className="message-content">
              {message.deleted_at ? (
                <>
                  {" "}
                  <GeckoIcon icon="comment-slash" />
                  &nbsp; This message has been removed.
                </>
              ) : (
                <>
                  <span className="message-sender d-block">
                    {message.from}
                    {isHost ? (
                      <span className="host-indicator">Host</span>
                    ) : null}
                    {isAdmin ? (
                      <span className="host-indicator">Organiser</span>
                    ) : null}
                  </span>
                  <HtmlWrapper html={message.content} />
                </>
              )}
            </span>
          </div>
        </div>
      </div>
    </AdminControl>
  );
};

const AdminControl = ({ children, message, setReplyTo, isLinkedMessage }) => {
  const [controlsShown, setControlsShown] = useState(false);

  // Dont render controls when NOT admin user
  if (!AdminService.isAdmin() || isLinkedMessage || message.deleted_at)
    return children;

  const contactUuid = ContactService.getContactUuid(message.contact_secret);

  const replyToMessage = () => {
    setReplyTo(message.uuid);
    setControlsShown(false);
  };

  const highlightMessageFn = () => {
    PresenceSocketService.trigger(
      MESSAGE_HIGHLIGHTED_EVENT,
      Object.assign(message, { fragmentId: VideoService.currentFragmentId() })
    );
    // Close dropdown
    setControlsShown(false);
  };

  const removeMessageFn = () => {
    PresenceSocketService.trigger(MESSAGE_REMOVED_EVENT, {
      uuid: message.uuid,
      deleted_at: DateUtils.utcNow(),
    });
    DataService.deleteStreamMessage(message.uuid);
    // Close dropdown
    setControlsShown(false);
  };

  const getAttendeeFn = () => {
    return DataService.get({
      account_uuid: $stateParams.account_uuid,
      event_id: $stateParams.event_id,
      contact_uuid: contactUuid,
    });
  };

  const removeAttendeeFn = () => {
    return AttendanceService.removeFn(contactUuid, EventService.eventId())
      .then(() => {
        PresenceSocketService.trigger(ATTENDEE_EJECTED_EVENT, {
          contact_secret: message.contact_secret,
          uuid: message.uuid,
          deleted_at: DateUtils.utcNow(),
        });
      })
      .catch(console.log);
  };

  const ejectAttendeeFn = () => {
    getAttendeeFn().then(({ contact }) => {
      const alertText = `
                ${__(STR_KEY.CHAT_ADMIN_CONTROL_MODAL_REMOVE_ATTENDEE_TITLE)}
                <br />
                <br />
                <strong>${__(STR_KEY.MISC_NAME)}:</strong> ${
        contact.full_name
      } (aka ${message.from})
                <br />
                <strong>${__(STR_KEY.MISC_EMAIL)}:</strong> ${contact.email}
            `;
      return new GeckoDialogService()
        .confirm(
          <HtmlWrapper style={{ wordBreak: "break-word" }} html={alertText} />,
          { btns: { confirm: { type: "primary" } } }
        )
        .then((ok) => {
          if (ok) return removeAttendeeFn();
        });
    });
    // Close dropdown
    setControlsShown(false);
  };

  const contactDetailsFn = () => {
    getAttendeeFn().then((data) =>
      new GeckoDialogService().alert(
        <div>
          {__(STR_KEY.CHAT_ADMIN_CONTROL_MODAL_CONTACT_DETAILS_TITLE)}{" "}
          <strong>{message.from}</strong>?
          <br />
          <br />
          <div style={{ wordBreak: "break-word" }}>
            <strong>{__(STR_KEY.MISC_NAME)}:</strong> {data.contact.full_name}
            <br />
            <strong>{__(STR_KEY.MISC_EMAIL)}:</strong> {data.contact.email}
          </div>
        </div>
      )
    );

    // Close dropdown
    setControlsShown(false);
  };

  return (
    <div
      onMouseEnter={() => setControlsShown(true)}
      onMouseLeave={() => setControlsShown(false)}
    >
      {children}
      {controlsShown && (
        <div className="message-menu-items">
          <MenuItem
            icon={"reply"}
            handler={replyToMessage}
            tooltip={__(STR_KEY.VIDEO_ADMIN_CONTROL_REPLY_TO_MESSAGE)}
          />
          <MenuItem
            icon={"star"}
            handler={highlightMessageFn}
            tooltip={__(STR_KEY.VIDEO_ADMIN_CONTROL_HIGHLIGHT_MESSAGE)}
          />
          {contactUuid && (
            <MenuItem
              icon={"address-book"}
              handler={contactDetailsFn}
              tooltip={__(STR_KEY.VIDEO_ADMIN_CONTROL_CONTACT_DETAILS)}
            />
          )}
          <MenuItem
            icon={"comment-alt-slash"}
            handler={removeMessageFn}
            tooltip={__(STR_KEY.VIDEO_ADMIN_CONTROL_REMOVE_MESSAGE)}
          />
          {contactUuid && (
            <MenuItem
              icon={"user-slash"}
              handler={ejectAttendeeFn}
              tooltip={__(STR_KEY.VIDEO_ADMIN_CONTROL_REMOVE_ATTENDEE)}
            />
          )}
        </div>
      )}
    </div>
  );
};

const MenuItem = ({ icon, handler, tooltip }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <button
      className="message-menu-item"
      id={`message-menu-item-${icon}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      icon={icon}
      onClick={handler}
      aria-label={tooltip}
    >
      <GeckoIcon
        style={
          isHovered
            ? GECKO_CONSTS.ICON_STYLE_REGULAR
            : GECKO_CONSTS.ICON_STYLE_LIGHT
        }
        icon={icon}
      />
      <Tooltip
        isOpen={isHovered}
        delay={0}
        placement="bottom"
        target={`message-menu-item-${icon}`}
      >
        {tooltip}
      </Tooltip>
    </button>
  );
};

export default Message;
